/* Сторінка «Оформлення замовлення» (/cart), sections/main-cart.liquid.
 *
 * 1. Права колонка `[data-ck-summary]` - живий кошик. Після кожної зміни кошика
 *    перемальовується лише вона (не вся секція), інакше губилося б введене в
 *    полях ліворуч. Як і шухляда, малюємо раз - коли кошик затих (драбина
 *    подарунків і промокод дають ланцюжок із 2-4 запитів).
 * 2. Ліва колонка - підготовка до чекауту Shopify: поля пишуться атрибутами
 *    кошика (коментар - приміткою), а при переході в чекаут ще й параметрами
 *    префілу `checkout[...]`.
 * 3. «Купити в один клік» - той самий перехід у чекаут, але без перевірки полів:
 *    що заповнено - піде, решту людина введе в чекауті (там же експрес-оплата).
 * 4. «Додати подарункове пакування» - кнопка з `data-packages-open`; попап
 *    відкриває assets/cart-packages.js делегованим кліком, тут нічого не треба.
 */
(() => {
  const SRC = 'cart-page';
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  class CartPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.pendingReq = 0;
      this.holds = 0;
      this.dirty = false;
      this.htmlFresh = false;

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => {
        const f = e.target.closest('.ckField');
        if (f && f.classList.contains('is-error')) this.clearError(f);
      });
      this.addEventListener('keydown', (e) => {
        const q = e.target.closest('[data-ck-qty]');
        if (q && e.key === 'Enter') { e.preventDefault(); q.blur(); }
      });
      this.form = this.querySelector('#ckForm');
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.checkout(false, e.submitter); });

      this.onRequest = () => { this.pendingReq++; this.dirty = true; this.setLoading(true); clearTimeout(this.quiet); };
      this.onHold = () => { this.holds++; clearTimeout(this.quiet); };
      this.onRelease = () => { this.holds = Math.max(0, this.holds - 1); this.schedule(); };
      document.addEventListener('cart:request', this.onRequest);
      document.addEventListener('cart:hold', this.onHold);
      document.addEventListener('cart:release', this.onRelease);

      this.unsub = window.PubSub?.subscribe(window.EVENTS.cartUpdate, ({ sections, source }) => {
        this.pendingReq = Math.max(0, this.pendingReq - 1);
        const html = sections?.[this.sectionId];
        if (html) { this.lastHtml = html; }
        this.htmlFresh = Boolean(html);
        this.dirty = true;
        if (source === 'promo-code' || source === 'promo-gift-sync') this.htmlFresh = false;
        this.schedule();
      });
      this.unsubErr = window.PubSub?.subscribe(window.EVENTS.cartError, () => {
        this.pendingReq = Math.max(0, this.pendingReq - 1);
        this.schedule();
      });
      window.CartAPI?.registerSection(this.sectionId);

      this.loadRates();
    }

    disconnectedCallback() {
      this.unsub?.();
      this.unsubErr?.();
      document.removeEventListener('cart:request', this.onRequest);
      document.removeEventListener('cart:hold', this.onHold);
      document.removeEventListener('cart:release', this.onRelease);
    }

    /* ---------- права колонка: кошик ---------- */

    summary() { return this.querySelector('[data-ck-summary]'); }

    setLoading(on) { this.summary()?.classList.toggle('is-loading', on); }

    schedule() {
      clearTimeout(this.quiet);
      if (!this.dirty) return;
      this.quiet = setTimeout(() => this.settle(), 300);
    }

    async settle() {
      if (this.pendingReq > 0 || this.holds > 0) return;
      let html = this.htmlFresh ? this.lastHtml : null;
      if (!html) {
        try {
          const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(this.sectionId)}`, { headers: { Accept: 'application/json' } });
          const data = await res.json();
          html = data?.[this.sectionId] || null;
        } catch (_) { html = this.lastHtml; }
      }
      if (this.pendingReq > 0 || this.holds > 0) return;
      if (html) this.render(html);
      this.lastHtml = null;
      this.dirty = false;
      this.setLoading(false);
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-page');
      if (!fresh) { window.location.reload(); return; }
      const freshSum = fresh.querySelector('[data-ck-summary]');
      const sum = this.summary();
      /* Порожній кошик або навпаки - змінюється вся сторінка. */
      if (!freshSum || !sum) { this.innerHTML = fresh.innerHTML; this.form = this.querySelector('#ckForm'); this.loadRates(); return; }
      const collapsed = sum.classList.contains('is-collapsed');
      sum.innerHTML = freshSum.innerHTML;
      sum.classList.toggle('is-collapsed', collapsed);
      sum.querySelector('[data-ck-toggle]')?.setAttribute('aria-expanded', String(!collapsed));
      const total = fresh.querySelector('[data-ck-total]');
      const mine = this.querySelector('[data-ck-total]');
      if (total && mine) mine.innerHTML = total.innerHTML;
    }

    onClick(e) {
      const step = e.target.closest('[data-ck-step]');
      if (step) {
        e.preventDefault();
        const input = step.parentElement.querySelector('[data-ck-qty]');
        if (!input) return;
        const next = (parseInt(input.value, 10) || 0) + Number(step.dataset.ckStep);
        if (next < 0) return;
        input.value = next;
        this.setQty(input, next);
        return;
      }
      const tog = e.target.closest('[data-ck-toggle]');
      if (tog) {
        const sum = this.summary();
        const collapsed = !sum.classList.contains('is-collapsed');
        sum.classList.toggle('is-collapsed', collapsed);
        tog.setAttribute('aria-expanded', String(!collapsed));
        return;
      }
      const fold = e.target.closest('[data-ck-fold]');
      if (fold) {
        const box = fold.closest('[data-ck-new]');
        const open = box.classList.toggle('is-folded') === false;
        fold.setAttribute('aria-expanded', String(open));
        return;
      }
      if (e.target.closest('[data-ck-oneclick]')) {
        e.preventDefault();
        this.checkout(true, e.target.closest('[data-ck-oneclick]'));
      }
    }

    onChange(e) {
      const q = e.target.closest('[data-ck-qty]');
      if (q) {
        this.setQty(q, Math.max(0, parseInt(q.value, 10) || 0));
        return;
      }
      if (e.target.form === this.form || e.target.getAttribute('form') === 'ckForm') this.saveSoon();
    }

    async setQty(input, paid) {
      if (this.busy) return;
      const line = Number(input.dataset.line);
      const offset = parseInt(input.dataset.offset, 10) || 0;
      this.busy = true;
      input.closest('.ckRow')?.classList.add('is-pending');
      try {
        await window.CartAPI.change({ line, quantity: paid + offset }, { source: SRC });
      } catch (_) {
        /* помилку покаже перерендер: кількість повернеться до справжньої */
        this.dirty = true;
        this.htmlFresh = false;
        this.schedule();
      } finally {
        this.busy = false;
      }
    }

    /* ---------- ліва колонка: поля ---------- */

    async loadRates() {
      const sel = this.querySelector('[data-ck-rates]');
      const url = this.dataset.rates;
      const iso = this.dataset.country;
      if (!sel || !url || !iso) return;
      try {
        const res = await fetch(`${url.replace(/\/$/, '')}/public/rates?iso=${encodeURIComponent(iso)}`);
        const data = await res.json();
        /* Тариф - перевізник × ваговий діапазон; у списку - лише перевізники.
           «Inpost postal machine A/B/C» - розміри одного й того самого способу. */
        const names = [...new Set((data.rates || []).map((r) => String(r.carrier || '').replace(/\s+[A-C]$/, '').trim()).filter(Boolean))];
        if (!names.length) return;
        const keep = sel.dataset.value || '';
        const head = sel.options[0].outerHTML;
        sel.innerHTML = head + names.map((n) => `<option value="${esc(n)}"${n === keep ? ' selected' : ''}>${esc(n)}</option>`).join('');
        if (keep && !names.includes(keep)) sel.insertAdjacentHTML('beforeend', `<option value="${esc(keep)}" selected>${esc(keep)}</option>`);
        sel.required = true;
      } catch (_) {
        /* воркер недоступний - список лишається порожнім і не обов'язковим */
      }
    }

    fields() {
      return [...this.querySelectorAll('input[form="ckForm"], select[form="ckForm"]')];
    }

    payload() {
      const attributes = {};
      let note = '';
      this.fields().forEach((el) => {
        if (el.name === 'note') { note = el.value.trim(); return; }
        const m = el.name.match(/^attributes\[(.+)\]$/);
        if (!m) return;
        attributes[m[1]] = el.type === 'checkbox' ? (el.checked ? 'yes' : '') : el.value.trim();
      });
      return { attributes, note };
    }

    saveSoon() {
      clearTimeout(this.saveT);
      this.saveT = setTimeout(() => this.save().catch(() => {}), 500);
    }

    /* Пишемо напряму в /cart/update.js, а не через CartAPI: атрибути не міняють
       ні суми, ні рядків, і перемальовувати кошик (та відкривати шухляду) не треба. */
    async save() {
      clearTimeout(this.saveT);
      const root = (window.routes?.cart_update_url || '/cart/update');
      const res = await fetch(`${root}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(this.payload()),
      });
      if (!res.ok) throw new Error(`cart update ${res.status}`);
    }

    clearError(field) {
      field.classList.remove('is-error');
      field.querySelector('.ckField__err')?.remove();
    }

    validate() {
      let first = null;
      const fold = this.querySelector('[data-ck-new]');
      this.fields().forEach((el) => {
        const field = el.closest('.ckField');
        if (!field) return;
        this.clearError(field);
        let msg = '';
        const v = String(el.value || '').trim();
        if (el.required && !v) msg = this.dataset.errRequired;
        else if (el.type === 'email' && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) msg = this.dataset.errEmail;
        if (!msg) return;
        field.classList.add('is-error');
        field.insertAdjacentHTML('beforeend', `<span class="ckField__err">${esc(msg)}</span>`);
        first = first || el;
      });
      if (first) {
        if (fold?.classList.contains('is-folded') && fold.contains(first)) {
          fold.classList.remove('is-folded');
          fold.querySelector('[data-ck-fold]')?.setAttribute('aria-expanded', 'true');
        }
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => first.focus({ preventScroll: true }), 300);
      }
      return !first;
    }

    /* Префіл чекауту: класичні параметри `checkout[...]`. Shopify бере їх і на
       /checkout, і на постійних посиланнях кошика; якщо магазин їх ігнорує, дані
       все одно лежать в атрибутах замовлення. */
    checkoutUrl() {
      const val = (k) => (this.querySelector(`[data-ck-key="${k}"]`)?.value || '').trim();
      const other = this.querySelector('[data-ck-other]')?.checked;
      const p = new URLSearchParams();
      const set = (k, v) => { if (v) p.set(k, v); };
      set('checkout[email]', val('email'));
      if (!other) {
        set('checkout[shipping_address][first_name]', val('first_name'));
        set('checkout[shipping_address][last_name]', val('last_name'));
        set('checkout[shipping_address][phone]', val('phone'));
      }
      set('checkout[shipping_address][city]', val('city'));
      if (p.toString()) set('checkout[shipping_address][country]', this.dataset.country);
      let root = this.dataset.root || '/';
      if (!root.endsWith('/')) root += '/';
      const qs = p.toString();
      return `${root}checkout${qs ? `?${qs}` : ''}`;
    }

    async checkout(quick, btn) {
      if (this.going) return;
      if (!quick && !this.validate()) return;
      this.going = true;
      btn?.classList.add('is-loading');
      try {
        await this.save();
      } catch (_) {
        /* атрибути не записались - у чекаут однаково ведемо */
      }
      window.location.href = this.checkoutUrl();
      setTimeout(() => { this.going = false; btn?.classList.remove('is-loading'); }, 4000);
    }
  }

  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);
})();
